
/****************************************************************************************
 * CampaignScreen.jsx – KPIs por rango + métricas de envío por campaña (17 Oct 2025)
 ****************************************************************************************/
import { useState, useEffect, useMemo } from "react";
import Papa from "papaparse";
import {
    db,
    collection,
    collectionGroup,
    query,
    where,
    onSnapshot,
    writeBatch,
    doc,
    storage,
} from "../firebase";
import { functions } from "../firebase";
import { httpsCallable } from "firebase/functions";
import {
    ref as storageRef,
    uploadBytes,
    getDownloadURL,
} from "firebase/storage";
import {
    getDocs,
    getDoc,
    setDoc,
    doc as fsDoc,
    getCountFromServer,
} from "firebase/firestore";
import axios from "axios";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";

/* ================== Config ================== */
const INFOBIP_KEY = "App 3322eedff04db1bc6657d2eda94bbebc-88a85d8a-ba13-4091-b143-08e62f3bdc30";
const SENDER = "12134250460";
const BASE_URL = "https://api.infobip.com";
const BATCH_SIZE = 500;

/* Utils */
export const normalisePhone = (raw = "") => {
    let d = String(raw).replace(/\D/g, "");
    if (d.startsWith("549")) return d;
    if (d.startsWith("54")) return "549" + d.slice(2);
    if (d.startsWith("0")) d = d.slice(1);
    return "549" + d;
};

const rangeToMs = (range) => {
    const days = range === "7d" ? 7 : range === "90d" ? 90 : 30;
    return Date.now() - days * 24 * 60 * 60 * 1000;
};

export default function CampaignScreen() {
    /* ── estado ── */
    const [campaigns, setCampaigns] = useState([]);
    const [loadingCampaigns, setLoadingCampaigns] = useState(true);

    const [showCampaignModal, setShowCampaignModal] = useState(false);
    const [showUploadModal, setShowUploadModal] = useState(false);
    const [showTemplateModal, setShowTemplateModal] = useState(false);
    const [showCampaignInfoModal, setShowCampaignInfoModal] = useState(false);
    const [selectedCampaign, setSelectedCampaign] = useState(null);

    /* Públicos */
    const [clientTypesData, setClientTypesData] = useState([]);
    const [loadingAudiences, setLoadingAudiences] = useState(true);
    const [selectedAudiences, setSelectedAudiences] = useState([]);
    const [selectedPhonesCount, setSelectedPhonesCount] = useState(0);

    /* Plantillas */
    const [templates, setTemplates] = useState([]);
    const [template, setTemplate] = useState("");
    const [templateBody, setTemplateBody] = useState("");
    const [templateHasPlaceholders, setTemplateHasPlaceholders] = useState(false);
    const [placeholdersInput, setPlaceholdersInput] = useState("");
    const [headerImagesMap, setHeaderImagesMap] = useState({});
    const [templateAssets, setTemplateAssets] = useState({});
    const [selectedHeaderUrl, setSelectedHeaderUrl] = useState("");
    const [loadingTemplates, setLoadingTemplates] = useState(false);

    const [sendingCampaign, setSendingCampaign] = useState(false);
    const [selectedChannel, setSelectedChannel] = useState("WhatsApp");

    /* KPIs por rango */
    const [kpiRange, setKpiRange] = useState("30d");
    const [kpiLoading, setKpiLoading] = useState(true);
    const [kpi, setKpi] = useState({
        messages: 0,
        campaigns: 0,
        templates: 0,
        avgAudience: 0,
        topTemplate: "-",
        audiencesUsed: 0,
    });

    /* ───────── Firestore: campañas ───────── */
    useEffect(() => {
        setLoadingCampaigns(true);
        const q = query(collection(db, "broadcastMessages"));
        const unsub = onSnapshot(
            q,
            (snap) => {
                const arr = snap.docs.map((d) => ({ id: d.id, ...d.data() }));
                arr.sort((a, b) => b.createdAt - a.createdAt);
                setCampaigns(arr);
                setLoadingCampaigns(false);
            },
            () => setLoadingCampaigns(false)
        );
        return () => unsub();
    }, []);

    /* ───────── Firestore: clientes ───────── */
    useEffect(() => {
        setLoadingAudiences(true);
        const unsub = onSnapshot(collection(db, "clients"), (snap) => {
            const map = {};
            snap.forEach((d) => {
                const data = d.data();
                const type = data.type || "Sin tipo";
                const phone = data.phoneNumber || data.phone || "";
                if (!map[type]) map[type] = { count: 0, phones: [] };
                map[type].count += 1;
                map[type].phones.push(phone);
            });
            const list = Object.keys(map)
                .sort((a, b) => a.localeCompare(b))
                .map((t) => ({ type: t, ...map[t] }));
            setClientTypesData(list);
            setLoadingAudiences(false);
        });
        return () => unsub();
    }, []);

    /* ───────── KPIs por rango ───────── */
    /* ───────── KPIs por rango (desde broadcastMessages) ───────── */
    useEffect(() => {
        (async () => {
            try {
                setKpiLoading(true);
                const since = rangeToMs(kpiRange);

                // Traemos todas las campañas del rango (podés filtrar por canal si querés)
                const qCamps = query(
                    collection(db, "broadcastMessages"),
                    where("createdAt", ">=", since)
                    // opcional: where("channel", "==", "WhatsApp")
                );
                const snap = await getDocs(qCamps);
                const rows = snap.docs.map((d) => d.data());

                const totalCamps = rows.length;

                // Suma de mensajes planificados (tu "envíos")
                let totalMessages = 0;
                let totalAudience = 0;

                // frecuencias de template y set de públicos usados
                const tplFreq = new Map();
                const audSet = new Set();

                rows.forEach((r) => {
                    const count = Number(r.targetAudienceCount || 0);
                    totalMessages += count;
                    totalAudience += count;

                    if (r.template) {
                        tplFreq.set(r.template, (tplFreq.get(r.template) || 0) + 1);
                    }

                    // Soporta ambos esquemas: targetAudiences[] (nuevo) o targetAudience (viejo)
                    if (Array.isArray(r.targetAudiences) && r.targetAudiences.length) {
                        r.targetAudiences.forEach((a) => a && audSet.add(String(a)));
                    } else if (r.targetAudience) {
                        audSet.add(String(r.targetAudience));
                    }
                });

                // Template más usado
                let topTemplate = "-";
                if (tplFreq.size) {
                    topTemplate = Array.from(tplFreq.entries()).sort((a, b) => b[1] - a[1])[0][0];
                }

                setKpi({
                    messages: totalMessages,
                    campaigns: totalCamps,
                    templates: tplFreq.size,
                    avgAudience: totalCamps ? Math.round(totalAudience / totalCamps) : 0,
                    topTemplate,
                    audiencesUsed: audSet.size,
                });
            } catch (e) {
                console.error("KPI error", e);
                // mantenemos el último valor sin romper UI
            } finally {
                setKpiLoading(false);
            }
        })();
    }, [kpiRange]);


    /* ───────── Placeholders helpers ───────── */
    const countPH = (t) => (t.match(/\{\{\d+\}\}/g) || []).length;
    const parseBodyPlaceholders = (input) =>
        input
            .split(",")
            .map((s) => s.trim())
            .reduce((acc, val, idx) => ({ ...acc, [idx + 1]: val }), {});

    /* ───────── Infobip helpers y envío ───────── */
    const buildInfobipMessage = ({ templateName, language = "en", headerType = "NONE", headerValue = "", bodyText = "", bodyPlaceholderValues = {}, buttons = [], to, }) => {
        const totalPH = (bodyText.match(/\{\{\d+\}\}/g) || []).length;
        const placeholders = Array.from({ length: totalPH }, (_, i) => bodyPlaceholderValues[i + 1] ?? "");
        let header;
        if (headerType === "IMAGE" && headerValue) header = { type: "IMAGE", mediaUrl: headerValue };
        else if (headerType === "TEXT" && headerValue) header = { type: "TEXT", placeholder: headerValue };
        const templateData = { body: { placeholders }, ...(header ? { header } : {}) };
        if (buttons.length) {
            buttons.forEach((b) => {
                if (b.type === "URL" && !b.parameter) throw new Error('Botón URL dinámico sin "parameter"');
            });
            templateData.buttons = buttons;
        }
        return { from: SENDER, to: normalisePhone(to), content: { templateName, language, templateData } };
    };

    const prepareButtons = (templateMeta, urlParamValues = {}) => {
        const btnDefs = templateMeta.structure?.buttons || [];
        const final = [];
        btnDefs.forEach((b) => {
            if (b.type === "URL") {
                if (/\{\{\d+\}\}/.test(b.url)) {
                    const idx = parseInt(b.url.match(/\{\{(\d+)\}\}/)[1], 10);
                    const param = urlParamValues[idx];
                    if (!param) throw new Error(`Falta el parámetro #${idx} para el botón URL de ${templateMeta.name}`);
                    final.push({ type: "URL", parameter: param });
                }
            } else if (b.type === "QUICK_REPLY") {
                if (b.parameter && b.parameter.trim()) final.push({ type: "QUICK_REPLY", parameter: b.parameter.trim() });
                else if (b.text && b.text.trim()) final.push({ type: "QUICK_REPLY", parameter: b.text.trim() });
                else final.push({ type: "QUICK_REPLY" });
            }
        });
        return final;
    };

    const sendWhatsAppMessages = async (messages) => {
        const chunks = [];
        for (let i = 0; i < messages.length; i += BATCH_SIZE) chunks.push(messages.slice(i, i + BATCH_SIZE));
        const requests = chunks.map((batch, i) =>
            axios
                .post(`${BASE_URL}/whatsapp/1/message/template`, { messages: batch }, {
                    headers: { Authorization: INFOBIP_KEY, "Content-Type": "application/json", Accept: "application/json" },
                })
                .catch((err) => {
                    console.error(`⛔️ Batch #${i} – ${err.response?.status} ${err.response?.statusText}`);
                    console.error(JSON.stringify(err.response?.data, null, 2));
                    throw err;
                })
        );
        return await Promise.allSettled(requests); // [{status, value|reason}]
    };

    /* ───────── cargar plantillas/headers ───────── */
    const fetchTemplates = async () => {
        try {
            setLoadingTemplates(true);
            const r = await axios.get(`${BASE_URL}/whatsapp/2/senders/${SENDER}/templates`, {
                headers: { Authorization: INFOBIP_KEY },
            });
            setTemplates(r.data.templates || []);
            const snap = await getDocs(collection(db, "waTemplateHeaders"));
            const assets = {};
            const imgMap = {};
            snap.forEach((d) => {
                const { images = [], urls = [] } = d.data() || {};
                assets[d.id] = { images, urls };
                imgMap[d.id] = images;
            });
            setTemplateAssets(assets);
            setHeaderImagesMap(imgMap);
        } catch {
            toast.error("Error cargando templates o imágenes");
        } finally {
            setLoadingTemplates(false);
        }
    };

    useEffect(() => {
        if (showCampaignModal || showTemplateModal) fetchTemplates();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [showCampaignModal, showTemplateModal]);

    /* ───────── audience union y conteo dedupe ───────── */
    useEffect(() => {
        if (!selectedAudiences.length) return setSelectedPhonesCount(0);
        const phoneSet = new Set();
        clientTypesData
            .filter((c) => selectedAudiences.includes(c.type))
            .forEach((grp) => {
                (grp.phones || []).forEach((raw) => {
                    const m = String(raw || "").match(/\d{6,15}/);
                    if (!m) return;
                    const norm = normalisePhone(m[0]);
                    if (/^549\d{10,11}$/.test(norm)) phoneSet.add(norm);
                });
            });
        setSelectedPhonesCount(phoneSet.size);
    }, [selectedAudiences, clientTypesData]);

    /* ───────── plantilla change ───────── */
    const handleTemplateChange = (e) => {
        const name = e.target.value;
        setTemplate(name);
        setSelectedHeaderUrl("");
        const meta = templates.find((t) => t.name === name);
        if (meta?.structure?.body?.text) {
            setTemplateBody(meta.structure.body.text);
            setTemplateHasPlaceholders(meta.structure.body.text.includes("{{"));
        } else {
            setTemplateBody("");
            setTemplateHasPlaceholders(false);
        }
    };

    /* ───────── crear / enviar campaña ───────── */
    const handleCreateCampaign = async () => {
        if (sendingCampaign) return;
        setSendingCampaign(true);
        try {
            if (selectedChannel !== "WhatsApp") {
                toast.error("Por ahora solo WhatsApp 🤝");
                return;
            }
            const meta = templates.find((t) => t.name === template);
            if (!meta) return toast.error("Template no encontrado");
            if (meta.status !== "APPROVED") return toast.error(`El template ${meta.name} está en estado ${meta.status}.`);

            // Header
            let headerUrl;
            if (meta.structure.header?.format === "IMAGE") {
                const assets = templateAssets[meta.name] || { images: [] };
                headerUrl = selectedHeaderUrl || assets.images?.[0]?.url;
                if (!headerUrl) return toast.error("Debes elegir una imagen para el header");
            }

            // Placeholders
            const bodyPlaceholderValues = templateHasPlaceholders ? parseBodyPlaceholders(placeholdersInput) : {};
            const phTotal = (meta.structure.body.text.match(/\{\{\d+\}\}/g) || []).length;
            for (let i = 1; i <= phTotal; i++) {
                if (!bodyPlaceholderValues[i] || !bodyPlaceholderValues[i].trim()) {
                    toast.error(`Falta el valor de {{${i}}}.`);
                    return;
                }
            }

            // Audiencias → teléfonos (union + dedupe)
            if (!selectedAudiences.length) return toast.error("Seleccioná al menos un público.");
            const phoneSet = new Set();
            clientTypesData
                .filter((g) => selectedAudiences.includes(g.type))
                .forEach((g) => {
                    (g.phones || []).forEach((raw) => {
                        const m = String(raw || "").match(/\d{6,15}/);
                        if (!m) return;
                        const norm = normalisePhone(m[0]);
                        if (/^549\d{10,11}$/.test(norm)) phoneSet.add(norm);
                    });
                });
            const phoneList = Array.from(phoneSet);
            if (!phoneList.length) return toast.error("No se encontraron teléfonos válidos.");

            // Build
            const messages = phoneList.map((phone) => {
                const urlParamValues = { 1: phone };
                const buttons = prepareButtons(meta, urlParamValues);
                return buildInfobipMessage({
                    templateName: meta.name,
                    language: meta.language || "en",
                    headerType: headerUrl ? "IMAGE" : "NONE",
                    headerValue: headerUrl || "",
                    bodyText: meta.structure.body.text,
                    bodyPlaceholderValues,
                    buttons,
                    to: phone,
                });
            });

            // Enviar (por lotes) y medir
            const results = await sendWhatsAppMessages(messages);
            const batchesTotal = results.length;
            const batchesFailed = results.filter((r) => r.status === "rejected").length;
            const batchesOk = batchesTotal - batchesFailed;
            const successRate = batchesTotal ? Math.round((batchesOk / batchesTotal) * 100) : 0;

            // Sample de errores (hasta 3)
            const errorSamples = results
                .filter((r) => r.status === "rejected")
                .slice(0, 3)
                .map((r) => ({
                    status: r?.reason?.response?.status || null,
                    statusText: r?.reason?.response?.statusText || null,
                    data: r?.reason?.response?.data || null,
                }));

            // Guardar campaña + métricas
            const campaignId = `${Date.now()}_${Math.random().toString(36).slice(2, 6)}`;
            const campaignRef = doc(db, "broadcastMessages", campaignId);
            await setDoc(campaignRef, {
                template: meta.name,
                channel: "WhatsApp",
                headerImageUrl: headerUrl || null,
                targetAudienceCount: messages.length,
                targetAudiences: selectedAudiences,
                createdAt: Date.now(),
                status: "sent",
                templateMessage: meta.structure?.body?.text || "",
                stats: {
                    batchesTotal,
                    batchesOk,
                    batchesFailed,
                    successRate, // %
                    messagesPlanned: messages.length,
                    errorSamples,
                },
            });

            // Subcolección messages (detalle)
            const batch = writeBatch(db);
            messages.forEach((m) => {
                const msgRef = doc(collection(campaignRef, "messages"));
                batch.set(msgRef, {
                    to: m.to,
                    templateName: m.content.templateName,
                    headerType: m.content.templateData.header?.type || "NONE",
                    headerMediaUrl: m.content.templateData.header?.mediaUrl || null,
                    placeholders: m.content.templateData.body.placeholders,
                    createdAt: Date.now(),
                    status: "sent",
                });
            });
            await batch.commit();

            if (batchesFailed) toast.error(`${batchesFailed}/${batchesTotal} lote(s) fallaron`);
            else toast.success("¡Mensajes enviados!");
            setShowCampaignModal(false);
            setTemplate("");
            setTemplateBody("");
            setTemplateHasPlaceholders(false);
            setPlaceholdersInput("");
            setSelectedHeaderUrl("");
            setSelectedAudiences([]);
            setSelectedPhonesCount(0);
        } catch (err) {
            console.error(err);
            toast.error(err.message);
        } finally {
            setSendingCampaign(false);
        }
    };

    /* ───────── UI ───────── */
    const Loader = ({ label = "Cargando…" }) => (
        <div className="flex items-center gap-2 text-sm opacity-70">
            <span className="loading loading-spinner loading-sm" />
            <span>{label}</span>
        </div>
    );

    return (
        <div className="p-6 space-y-6">
            <div className="flex items-center justify-between">
                <h1 className="text-3xl font-bold">Campañas salientes</h1>
                <div className="flex gap-2">
                    <button className="btn bg-[#e91c24] text-white border-none" onClick={() => setShowCampaignModal(true)}>
                        Nueva campaña
                    </button>
                    <button className="btn bg-base-200" onClick={() => setShowUploadModal(true)}>
                        Cargar público
                    </button>
                    <button className="btn bg-base-200" onClick={() => setShowTemplateModal(true)}>
                        Nuevo template
                    </button>
                </div>
            </div>

            {/* KPIs + selector de rango */}
            <div className="flex items-center justify-between">
                <h2 className="text-lg font-bold">Indicadores</h2>
                <div className="flex items-center gap-2">
                    <span className="text-sm opacity-70">Rango:</span>
                    <select
                        className="select select-bordered select-sm"
                        value={kpiRange}
                        onChange={(e) => setKpiRange(e.target.value)}
                    >
                        <option value="7d">Últimos 7 días</option>
                        <option value="30d">Últimos 30 días</option>
                        <option value="90d">Últimos 90 días</option>
                    </select>
                </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 xl:grid-cols-6 gap-3">
                <KpiCard title={`Mensajes ${kpiRange}`} value={kpi.messages} loading={kpiLoading} />
                <KpiCard title={`Campañas ${kpiRange}`} value={kpi.campaigns} loading={kpiLoading} />
                <KpiCard title={`Templates únicos ${kpiRange}`} value={kpi.templates} loading={kpiLoading} />
                <KpiCard title={`Audiencia prom. ${kpiRange}`} value={kpi.avgAudience} loading={kpiLoading} />
                <KpiCard title={`Top template ${kpiRange}`} value={kpi.topTemplate} loading={kpiLoading} />
                <KpiCard title={`Públicos usados ${kpiRange}`} value={kpi.audiencesUsed} loading={kpiLoading} />
            </div>

            {/* Historial */}
            <div className="bg-base-100 p-4 rounded-2xl shadow-sm">
                <div className="flex items-center justify-between mb-3">
                    <h2 className="text-lg font-bold">Historial</h2>
                    {loadingCampaigns && <Loader />}
                </div>

                {!loadingCampaigns && !campaigns.length && (
                    <div className="text-sm opacity-70">No hay campañas.</div>
                )}

                {!!campaigns.length && (
                    <div className="overflow-x-auto">
                        <table className="table w-full">
                            <thead>
                                <tr>
                                    <th>Fecha</th>
                                    <th>Detalle</th>
                                    <th>Canal</th>
                                    <th>Público(s)</th>
                                    <th>Cantidad</th>
                                    {/* <th>Éxito</th> */}
                                </tr>
                            </thead>
                            <tbody>
                                {campaigns.map((c) => (
                                    <tr
                                        key={c.id}
                                        className="hover"
                                        style={{ cursor: "pointer" }}
                                        onClick={() => {
                                            setSelectedCampaign(c);
                                            setShowCampaignInfoModal(true);
                                        }}
                                    >
                                        <td>{new Date(c.createdAt).toLocaleString()}</td>
                                        <td>{c.channel === "WhatsApp" ? c.template : <>Asunto: {c.subject}</>}</td>
                                        <td>{c.channel}</td>
                                        <td className="max-w-[280px]">
                                            {Array.isArray(c.targetAudiences) ? c.targetAudiences.join(", ") : c.targetAudience || "-"}
                                        </td>
                                        <td>{c.targetAudienceCount}</td>
                                        {/* <td>
                                            {c.stats ? (
                                                <span className={c.stats.successRate >= 100 ? "text-success" : c.stats.successRate >= 80 ? "text-warning" : "text-error"}>
                                                    {c.stats.successRate}%
                                                </span>
                                            ) : (
                                                "-"
                                            )}
                                        </td> */}
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {/* Modales */}
            {showCampaignModal && (
                <CampaignCreatorModal
                    {...{
                        selectedChannel,
                        setSelectedChannel,
                        templates,
                        template,
                        handleTemplateChange,
                        templateBody,
                        templateHasPlaceholders,
                        placeholdersInput,
                        setPlaceholdersInput,
                        clientTypesData,
                        selectedAudiences,
                        setSelectedAudiences,
                        selectedPhonesCount,
                        handleCreateCampaign,
                        setShowCampaignModal,
                        headerImagesMap,
                        selectedHeaderUrl,
                        setSelectedHeaderUrl,
                        sendingCampaign,
                        loadingTemplates,
                        loadingAudiences,
                    }}
                />
            )}

            {showCampaignInfoModal && selectedCampaign && (
                <CampaignInfoModal campaign={selectedCampaign} onClose={() => setShowCampaignInfoModal(false)} />
            )}

            {showUploadModal && <UploadAudienceModal onClose={() => setShowUploadModal(false)} normalisePhone={normalisePhone} />}
            {showTemplateModal && <TemplateBuilderModal onClose={() => { setShowTemplateModal(false); fetchTemplates(); }} />}
        </div>
    );
}

/* ───────── Sub-componentes ───────── */
function KpiCard({ title, value, loading }) {
    return (
        <div className="bg-base-100 p-4 rounded-2xl shadow-sm">
            <p className="text-xs opacity-70">{title}</p>
            <div className="mt-1 text-2xl font-semibold tracking-tight">
                {loading ? <span className="loading loading-dots loading-md" /> : value}
            </div>
        </div>
    );
}

function CampaignCreatorModal(props) {
    const {
        selectedChannel,
        setSelectedChannel,
        templates,
        template,
        handleTemplateChange,
        templateBody,
        templateHasPlaceholders,
        placeholdersInput,
        setPlaceholdersInput,
        clientTypesData,
        selectedAudiences,
        setSelectedAudiences,
        selectedPhonesCount,
        handleCreateCampaign,
        setShowCampaignModal,
        headerImagesMap,
        selectedHeaderUrl,
        setSelectedHeaderUrl,
        sendingCampaign,
        loadingTemplates,
        loadingAudiences,
    } = props;

    const toggleAudience = (t) =>
        setSelectedAudiences((prev) => (prev.includes(t) ? prev.filter((x) => x !== t) : [...prev, t]));

    return (
        <div className="modal modal-open">
            <div className="modal-box w-full max-w-3xl">
                <h3 className="font-bold text-xl mb-6">Crear campaña</h3>

                <div className="form-control mb-4">
                    <p className="font-semibold mb-1">Canal</p>
                    <select className="select select-bordered w-full" value={selectedChannel} onChange={(e) => setSelectedChannel(e.target.value)}>
                        <option value="WhatsApp">WhatsApp</option>
                    </select>
                </div>

                <div className="form-control mb-3">
                    <div className="flex items-center justify-between">
                        <p className="font-semibold mb-1">Template</p>
                        {loadingTemplates && <span className="loading loading-spinner loading-xs" />}
                    </div>
                    <select className="select select-bordered w-full" value={template} onChange={handleTemplateChange}>
                        <option value="">Selecciona un template</option>
                        {templates.map((t) => (
                            <option key={t.name} value={t.name}>
                                {t.name} — {t.status === "APPROVED" ? "✅" : t.status === "PENDING" ? "⏳" : "❌"}
                            </option>
                        ))}
                    </select>
                </div>

                {!!templateBody && (
                    <div className="bg-base-200 p-3 rounded mb-3">
                        <p className="font-bold">Mensaje del template:</p>
                        <p className="whitespace-pre-wrap">{templateBody}</p>
                    </div>
                )}

                {(() => {
                    const tmpl = templates.find((t) => t.name === template);
                    if (tmpl?.structure?.header?.format !== "IMAGE") return null;
                    const imgs = headerImagesMap[tmpl.name] || [];
                    return (
                        <div className="form-control mb-3">
                            <p className="font-semibold mb-1">Imagen de header</p>
                            {!imgs.length ? (
                                <p className="text-warning">No hay imágenes cargadas para este template.</p>
                            ) : (
                                <select className="select select-bordered" value={selectedHeaderUrl} onChange={(e) => setSelectedHeaderUrl(e.target.value)}>
                                    <option value="">Selecciona…</option>
                                    {imgs.map((img) => (
                                        <option key={img.url} value={img.url}>
                                            {img.label}
                                        </option>
                                    ))}
                                </select>
                            )}
                        </div>
                    );
                })()}

                {templateHasPlaceholders && (
                    <div className="form-control mb-4">
                        <div className="flex items-center justify-between">
                            <p className="font-semibold mb-1">Placeholders (separados por comas)</p>
                            <span className="badge badge-ghost text-xs">
                                Total: {templateBody.match(/\{\{\d+\}\}/g)?.length || 0}
                            </span>
                        </div>
                        <input
                            type="text"
                            className="input input-bordered"
                            value={placeholdersInput}
                            onChange={(e) => setPlaceholdersInput(e.target.value)}
                            placeholder="Ej: Juan,Perez,1234"
                        />
                    </div>
                )}

                <div className="form-control mb-4">
                    <div className="flex items-center justify-between">
                        <p className="font-semibold mb-1">Públicos</p>
                        {loadingAudiences && <span className="loading loading-spinner loading-xs" />}
                    </div>
                    <div className="bg-base-200 rounded p-3 max-h-56 overflow-auto">
                        {clientTypesData.map((c) => (
                            <label key={c.type} className="flex items-center gap-2 py-1 cursor-pointer">
                                <input
                                    type="checkbox"
                                    className="checkbox checkbox-sm"
                                    checked={selectedAudiences.includes(c.type)}
                                    onChange={() => toggleAudience(c.type)}
                                />
                                <span className="text-sm">{c.type}</span>
                                <span className="badge badge-ghost">{c.count}</span>
                            </label>
                        ))}
                    </div>
                    <div className="mt-2 text-sm opacity-80">
                        Total teléfonos únicos estimados: <span className="font-semibold">{selectedPhonesCount}</span>
                    </div>
                </div>

                <div className="modal-action">
                    <button className="btn" onClick={() => setShowCampaignModal(false)}>
                        Cancelar
                    </button>
                    <button
                        className="btn bg-[#e91c24] text-white border-none"
                        onClick={handleCreateCampaign}
                        disabled={
                            sendingCampaign ||
                            selectedChannel !== "WhatsApp" ||
                            !template ||
                            !selectedAudiences.length ||
                            (templates.find((t) => t.name === template)?.structure?.header?.format === "IMAGE" && !selectedHeaderUrl)
                        }
                    >
                        {sendingCampaign ? (
                            <>
                                <span className="loading loading-spinner loading-sm mr-2" />
                                Enviando…
                            </>
                        ) : (
                            "Enviar campaña"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

function CampaignInfoModal({ campaign, onClose }) {
    return (
        <div className="modal modal-open">
            <div className="modal-box max-w-xl">
                <div className="p-1 space-y-3">
                    <h3 className="font-bold text-2xl">Detalle</h3>
                    <p><strong>Fecha:</strong> {new Date(campaign.createdAt).toLocaleString()}</p>
                    <p><strong>Canal:</strong> {campaign.channel}</p>
                    <p>
                        <strong>Público(s):</strong>{" "}
                        {Array.isArray(campaign.targetAudiences) ? campaign.targetAudiences.join(", ") : campaign.targetAudience || "-"}{" "}
                        ({campaign.targetAudienceCount})
                    </p>
                    {campaign.channel === "WhatsApp" && (
                        <>
                            <p><strong>Template:</strong> {campaign.template}</p>
                            <p className="whitespace-pre-wrap">
                                <strong>Mensaje:</strong> {campaign.templateMessage || "No disponible"}
                            </p>
                            {campaign.stats && (
                                <div className="bg-base-200 rounded p-3 text-sm">
                                    <p className="font-semibold mb-1">Resumen de envío</p>
                                    <ul className="list-disc list-inside space-y-1">
                                        <li>Lotes: {campaign.stats.batchesOk}/{campaign.stats.batchesTotal} OK</li>
                                        <li>Fallidos: {campaign.stats.batchesFailed}</li>
                                        <li>Éxito: {campaign.stats.successRate}%</li>
                                        <li>Mensajes planificados: {campaign.stats.messagesPlanned}</li>
                                    </ul>
                                    {campaign.stats.errorSamples?.length ? (
                                        <>
                                            <p className="mt-2 font-semibold">Ejemplos de error:</p>
                                            <pre className="text-xs overflow-auto max-h-40">
                                                {JSON.stringify(campaign.stats.errorSamples, null, 2)}
                                            </pre>
                                        </>
                                    ) : null}
                                </div>
                            )}
                        </>
                    )}
                </div>
                <div className="modal-action">
                    <button className="btn" onClick={onClose}>Cerrar</button>
                </div>
            </div>
        </div>
    );
}

function UploadAudienceModal({ onClose, normalisePhone }) {
    const [file, setFile] = useState(null);
    const [preview, setPreview] = useState([]);
    const [uploading, setUploading] = useState(false);
    const [audienceType, setAudienceType] = useState("");

    const handleFile = (e) => {
        const f = e.target.files[0];
        if (!f) return;
        setFile(f);

        // Parse CSV (usa PapaParse)
        Papa.parse(f, {
            header: true,
            skipEmptyLines: true,
            complete: (results) => {
                const rows = results.data || [];
                // buscar columnas que parezcan teléfono
                const detected = rows.map((r) => {
                    const phoneCandidate =
                        r.phone ||
                        r.Phone ||
                        r.telefono ||
                        r.Teléfono ||
                        r.celular ||
                        r.Celular ||
                        r["número"] ||
                        r["Número"];
                    return {
                        name:
                            r.name ||
                            r.Nombre ||
                            r.nombre ||
                            r["razon social"] ||
                            r["Razon social"] ||
                            "",
                        phone: phoneCandidate ? normalisePhone(phoneCandidate) : "",
                    };
                });
                setPreview(detected.slice(0, 20)); // solo los primeros 20 para vista previa
            },
            error: (err) => {
                console.error("Error parseando CSV", err);
                toast.error("Error leyendo el archivo");
            },
        });
    };

    const handleUpload = async () => {
        if (!file) return toast.error("Selecciona un archivo CSV");
        if (!audienceType.trim()) return toast.error("Escribe un nombre para el público");
        if (!preview.length) return toast.error("No se detectaron registros válidos");

        try {
            setUploading(true);

            const snap = await getDocs(collection(db, "clients"));
            const batch = writeBatch(db);

            let added = 0;
            preview.forEach((row, idx) => {
                if (!row.phone || row.phone.length < 8) return;
                const docRef = doc(collection(db, "clients"));
                batch.set(docRef, {
                    name: row.name || "",
                    phoneNumber: row.phone,
                    type: audienceType,
                    createdAt: Date.now(),
                    source: "upload",
                });
                added++;
            });

            await batch.commit();
            toast.success(`Se cargaron ${added} contactos en "${audienceType}"`);
            onClose();
        } catch (e) {
            console.error(e);
            toast.error("Error subiendo contactos");
        } finally {
            setUploading(false);
        }
    };

    return (
        <div className="modal modal-open">
            <div className="modal-box w-full max-w-lg">
                <h3 className="font-bold text-xl mb-4">Cargar público</h3>

                <div className="form-control mb-4">
                    <p className="font-semibold mb-1">Archivo CSV</p>
                    <input
                        type="file"
                        accept=".csv"
                        className="file-input file-input-bordered w-full"
                        onChange={handleFile}
                        disabled={uploading}
                    />
                    <p className="text-xs opacity-70 mt-1">
                        El archivo debe tener columnas “phone” o “telefono”.
                    </p>
                </div>

                <div className="form-control mb-4">
                    <p className="font-semibold mb-1">Nombre del público</p>
                    <input
                        type="text"
                        className="input input-bordered w-full"
                        placeholder="Ej: Clientes Recurrentes"
                        value={audienceType}
                        onChange={(e) => setAudienceType(e.target.value)}
                        disabled={uploading}
                    />
                </div>

                {preview.length > 0 && (
                    <div className="bg-base-200 rounded p-3 mb-4 max-h-48 overflow-auto">
                        <p className="font-semibold text-sm mb-1">
                            Vista previa ({preview.length > 20 ? "primeros 20" : preview.length} registros)
                        </p>
                        <table className="table table-xs w-full">
                            <thead>
                                <tr>
                                    <th>Nombre</th>
                                    <th>Teléfono</th>
                                </tr>
                            </thead>
                            <tbody>
                                {preview.map((r, i) => (
                                    <tr key={i}>
                                        <td>{r.name || "-"}</td>
                                        <td>{r.phone}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    </div>
                )}

                <div className="modal-action">
                    <button className="btn" onClick={onClose} disabled={uploading}>
                        Cancelar
                    </button>
                    <button
                        className="btn bg-[#e91c24] text-white border-none"
                        onClick={handleUpload}
                        disabled={uploading || !file || !audienceType}
                    >
                        {uploading ? (
                            <>
                                <span className="loading loading-spinner loading-sm mr-2" />
                                Subiendo…
                            </>
                        ) : (
                            "Subir público"
                        )}
                    </button>
                </div>
            </div>
        </div>
    );
}

function TemplateBuilderModal({ onClose }) {
    const [name, setName] = useState("");
    const [headerImageUrl, setHeaderImageUrl] = useState("");
    const [headerFileName, setHeaderFileName] = useState("");
    const [bodyText, setBodyText] = useState("¡Hola! ¿Cómo estás?");
    const [buttons, setButtons] = useState([]);

    const [uploading, setUploading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rephrasing, setRephrasing] = useState(false);

    // subir imagen de header (a Firebase Storage)
    const handleImageFile = async (e) => {
        const file = e.target.files[0];
        if (!file) return;
        try {
            setUploading(true);
            const path = `templates/headerImages/${Date.now()}_${file.name}`;
            const ref = storageRef(storage, path);
            await uploadBytes(ref, file, {
                contentType: file.type,
                customMetadata: { uploadedBy: "template_builder" },
            });
            const url = await getDownloadURL(ref);
            setHeaderImageUrl(url);
            setHeaderFileName(file.name);
            toast.success("Imagen subida ✅");
        } catch (err) {
            console.error(err);
            toast.error("Error subiendo imagen");
        } finally {
            setUploading(false);
        }
    };

    // helpers botones
    const addButton = () => setButtons((prev) => [...prev, { type: "QUICK_REPLY", text: "", url: "" }]);
    const updateBtn = (i, k, v) => setButtons((prev) => prev.map((b, idx) => (idx === i ? { ...b, [k]: v } : b)));
    const removeBtn = (i) => setButtons((prev) => prev.filter((_, idx) => idx !== i));

    const normalizeTemplateName = (raw = "") =>
        raw
            .normalize("NFD")
            .replace(/[\u0300-\u036f]/g, "")
            .replace(/\s+/g, "_")
            .replace(/-/g, "_")
            .replace(/[^A-Za-z0-9_]/g, "")
            .toLowerCase();

    // 👇 IA: llama al callable v2 onCall "aiRephraseText"
    const aiRephrase = async () => {
        try {
            setRephrasing(true);
            const callable = httpsCallable(functions, "aiRephraseText");
            const { data } = await callable({
                text: bodyText,
                tone: "cálido, cercano, claro, con CTA breve",
                channel: "whatsapp",
                maxChars: 1000,
            });
            if (data?.ok && data?.text) {
                setBodyText(data.text);
                toast.success("Texto mejorado ✨");
            } else {
                toast.error(data?.error || "No se pudo reformular");
            }
        } catch (e) {
            console.error(e);
            toast.error("Error llamando a la IA");
        } finally {
            setRephrasing(false);
        }
    };

    // crea el template en Infobip y guarda assets en Firestore
    const saveTemplate = async () => {
        if (saving) return;
        if (!name || !bodyText) return toast.error("Completa nombre y body");
        setSaving(true);

        const templateName = normalizeTemplateName(name);
        const headerType = headerImageUrl ? "IMAGE" : "NONE";
        const placeholderRegex = /\{\{\d+\}\}/g;
        const phCount = (bodyText.match(placeholderRegex) || []).length;

        try {
            // 1) guardar imagen de header (si hay)
            if (headerType === "IMAGE") {
                const docRef = fsDoc(db, "waTemplateHeaders", templateName);
                const snap = await getDoc(docRef);
                const data = snap.exists() ? snap.data() : {};
                const images = Array.isArray(data.images) ? data.images : [];
                if (!images.some((img) => img.url === headerImageUrl)) {
                    images.push({ url: headerImageUrl, label: headerFileName || "Header" });
                }
                await setDoc(docRef, { images }, { merge: true });
            }

            // 2) armar estructura Infobip
            const bodyBlock = { text: bodyText };
            if (phCount) {
                bodyBlock.examples = phCount === 1 ? ["example"] : Array.from({ length: phCount }, (_, i) => `ex${i + 1}`);
            }

            let headerBlock;
            if (headerType === "IMAGE" && headerImageUrl) {
                headerBlock = { format: "IMAGE", example: headerImageUrl };
            }

            let buttonsBlock;
            if (buttons.length) {
                buttonsBlock = buttons.map((btn, idx) => {
                    if (btn.type === "URL") {
                        const exampleResolved = placeholderRegex.test(btn.url)
                            ? btn.url.replace(placeholderRegex, "sample")
                            : btn.url;
                        return { type: "URL", text: btn.text || `Button ${idx + 1}`, url: btn.url, example: exampleResolved };
                    }
                    return { type: "QUICK_REPLY", text: btn.text || `Button ${idx + 1}` };
                });
            }

            const payload = {
                name: templateName,
                language: "en",
                category: "MARKETING",
                structure: {
                    body: bodyBlock,
                    type: headerBlock ? "MEDIA" : "TEXT",
                    ...(headerBlock ? { header: headerBlock } : {}),
                    ...(buttonsBlock ? { buttons: buttonsBlock } : {}),
                },
            };

            // 3) crear template en Infobip
            const res = await axios.post(
                `${BASE_URL}/whatsapp/2/senders/${SENDER}/templates`,
                payload,
                {
                    headers: {
                        Authorization: INFOBIP_KEY,
                        "Content-Type": "application/json",
                        Accept: "application/json",
                    },
                }
            );

            toast.success(`Template enviado a revisión (${res.data.status || "OK"})`);
            onClose();
        } catch (err) {
            console.error(err);
            toast.error(err.response?.data?.message || err.message || "Error creando template");
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="modal modal-open">
            <div className="modal-box w-full max-w-3xl">
                <h3 className="font-bold text-xl mb-4">Nuevo template WhatsApp</h3>

                <Input label="Nombre (minúsculas + _)" value={name} onChange={setName} />

                {/* Header imagen (opcional) */}
                <div className="form-control mb-4">
                    <p className="font-semibold mb-1">Imagen (opcional)</p>
                    <input type="file" accept="image/*" className="file-input" onChange={handleImageFile} disabled={uploading} />
                    {uploading && <progress className="progress w-full mt-2"></progress>}
                    {headerImageUrl && <p className="text-xs mt-1 break-all">{headerImageUrl}</p>}
                </div>

                {/* Body + IA */}
                <div className="form-control mb-2">
                    <div className="flex items-center justify-between">
                        <p className="font-semibold mb-1">Mensaje</p>
                        <button className="btn btn-ghost btn-xs" onClick={aiRephrase} disabled={rephrasing}>
                            {rephrasing ? (<><span className="loading loading-spinner loading-xs mr-1" /> Mejorando…</>) : ("Mejorar con IA ✨")}
                        </button>
                    </div>
                    <textarea className="textarea textarea-bordered w-full min-h-28" value={bodyText} onChange={(e) => setBodyText(e.target.value)} />
                    <p className="text-xs opacity-70 mt-1">Placeholders detectados: {bodyText.match(/\{\{\d+\}\}/g)?.length || 0}</p>
                </div>

                {/* Botones */}
                <div className="mb-4">
                    <p className="font-semibold mb-1">Botones</p>
                    {buttons.map((b, i) => (
                        <div key={i} className="flex items-center mb-2 gap-2">
                            <select className="select select-bordered select-sm" value={b.type} onChange={(e) => updateBtn(i, "type", e.target.value)}>
                                <option value="QUICK_REPLY">QUICK_REPLY</option>
                                <option value="URL">URL</option>
                            </select>
                            <input type="text" className="input input-bordered input-sm flex-1" placeholder="Texto" value={b.text} onChange={(e) => updateBtn(i, "text", e.target.value)} />
                            {b.type === "URL" && (
                                <input type="text" className="input input-bordered input-sm flex-1" placeholder="https://… (admite {{1}})" value={b.url} onChange={(e) => updateBtn(i, "url", e.target.value)} />
                            )}
                            <button className="btn btn-xs" onClick={() => removeBtn(i)}>✕</button>
                        </div>
                    ))}
                    <button className="btn btn-sm mt-1" onClick={addButton}>+ Añadir botón</button>
                </div>

                <div className="modal-action">
                    <button className="btn" onClick={onClose}>Cancelar</button>
                    <button className="btn bg-[#e91c24] text-white border-none" onClick={saveTemplate} disabled={saving || uploading}>
                        {saving ? (<><span className="loading loading-spinner loading-sm mr-2" /> Enviando…</>) : ("Crear template")}
                    </button>
                </div>
            </div>
        </div>
    );
}


/* Inputs genéricos */
function Input({ label, value, onChange, placeholder = "" }) {
    return (
        <div className="form-control mb-4">
            <p className="font-semibold mb-1">{label}</p>
            <input
                type="text"
                className="input input-bordered w-full"
                value={value}
                placeholder={placeholder}
                onChange={(e) => onChange(e.target.value)}
            />
        </div>
    );
}
